import { PlusIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { TableViewButton } from "@/components/TableViewButton";
import { Button } from "@/components/ui/button";

import { useCreateView } from "@/hooks/mutations/useCreateView";
import { useAddView } from "@/hooks/sets/useAddView";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { mutations } from "@/lib/mutations";
import { QueryParamsType, ViewQueryParams, ViewTableName } from "@/types";

interface TableViewsView {
  filterState?: unknown;
  id: number | string;
  isDefault?: boolean;
  name: string;
  queryParams?: unknown;
}

export function TableViews<T extends TableViewsView>({
  defaultQueryParams,
  jobId,
  viewCounts,
  selectedView,
  setSelectedView,
  tableName,
  viewQueryParams,
  views,
}: {
  defaultQueryParams: QueryParamsType;
  jobId?: string;
  viewCounts?: Record<string, number>;
  selectedView?: T;
  setSelectedView: (view: T) => void;
  tableName: ViewTableName;
  viewQueryParams: ViewQueryParams;
  views: T[];
}) {
  /* Database Queries and Cache Updates */
  // Creates
  const { mutate: createView } = useCreateView();
  const addView = useAddView();

  const handleCreateView = () => {
    mixpanelUtils.trackButtonClick(
      "Create new saved view",
      getScreenFromPathname(location.pathname),
    );
    mutations.createView({
      addView,
      createView,
      createViewRequest: { jobId, queryParams: defaultQueryParams, tableName },
      setSelectedView: (newView) => {
        setSelectedView(newView as unknown as T);
      },
      viewQueryParams,
    });
  };

  const handleSelectView = (view: T) => {
    mixpanelUtils.trackViewClick(tableName, view.name, {
      value: JSON.stringify(view.filterState ?? view.queryParams),
    });
    setSelectedView(view);
  };

  const render = () => (
    <>
      <Row className="flex-wrap gap-2">
        {views.map((view) => {
          return (
            <TableViewButton
              handleSelectView={handleSelectView}
              key={String(view.id)}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              view={view}
              viewCounts={viewCounts}
              viewQueryParams={viewQueryParams}
              views={views}
            />
          );
        })}
        <Button
          onClick={handleCreateView}
          variant="outlineSecondary"
          className="h-8 border-0 hover:bg-primary-200"
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </Row>
      <div className="h-px w-full bg-slate-200" />
    </>
  );

  return render();
}
